import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Calculator, Crosshair } from "lucide-react";
import { scrollToId } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1753194931766-211c9f563156?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBmbG9yaWRhJTIwaG9tZSUyMGV4dGVyaW9yJTIwcG9vbHxlbnwwfHx8fDE3OTA0NDc5MDF8MA&ixlib=rb-4.1.0&q=85";

const lines = ["DEFENDING YOUR", "FLORIDA HOME", "BEFORE YOU BUY,", "BUILD, OR INSURE."];

const points = [
  { x: "30%", y: "22%", label: "ROOF SYSTEM", code: "SEC-01" },
  { x: "68%", y: "34%", label: "WIND MITIGATION", code: "SEC-02" },
  { x: "24%", y: "64%", label: "FOUNDATION", code: "SEC-03" },
  { x: "72%", y: "70%", label: "ELECTRICAL 4-PT", code: "SEC-04" },
];

const stats = ["1,400+ HOMES INSPECTED", "100% FL STANDARD COMPLIANT", "SAME-DAY DIGITAL REPORTS"];

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const [active, setActive] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const gridY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} className="relative min-h-screen bg-obsidian noise overflow-hidden flex items-center">
      <motion.div style={{ y: gridY }} className="absolute inset-0 blueprint-grid" />
      <div className="absolute -top-32 -right-32 w-[540px] h-[540px] rounded-full bg-sun/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-24 grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="inline-flex items-center gap-2.5 border border-sand/15 rounded-full px-4 py-2 mb-8"
            data-testid="hero-license-badge"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
            <a
              href="https://www.myfloridalicense.com/wl11.asp"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-license-verify-link"
              className="font-mono text-[11px] tracking-[0.25em] text-sand/80 uppercase underline-offset-4 transition-colors hover:text-sun hover:underline"
            >
              FL Licensed &amp; Insured — #HI19148
            </a>
          </motion.div>

          <h1
            data-testid="hero-heading"
            className="font-display font-black text-sand text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]"
          >
            {lines.map((l, i) => (
              <RevealLine key={l} delay={0.35 + i * 0.13}>
                {i === 3 ? (
                  <span>
                    BUILD, OR <span className="text-sun">INSURE.</span>
                  </span>
                ) : (
                  l
                )}
              </RevealLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-7 text-sand/60 text-base sm:text-lg max-w-xl leading-relaxed"
          >
            Precision residential inspections across Florida — 300+ point evaluations,
            insurance-ready 4-point and wind mitigation reports, delivered the same day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-now-button"
              onClick={() => onBook()}
              className="group flex items-center gap-3 bg-sun text-obsidian font-semibold px-7 py-4 rounded-full transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            >
              Book Inspection Online
              <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button
              data-testid="hero-calculator-button"
              onClick={() => scrollToId("calculator")}
              className="group flex items-center gap-3 border border-sand/25 text-sand font-semibold px-7 py-4 rounded-full transition-colors duration-300 hover:border-sun hover:text-sun"
            >
              <Calculator size={17} />
              Calculate Inspection Cost
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-3"
          >
            {stats.map((s) => (
              <div key={s} className="flex items-center gap-2.5">
                <Crosshair size={13} className="text-sun" />
                <span className="font-mono text-[11px] tracking-[0.2em] text-sand/50 uppercase">{s}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92, clipPath: "inset(100% 0 0 0)" }}
          animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0 0 0)" }}
          transition={{ delay: 0.7, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
        >
          <div className="relative rounded-2xl overflow-hidden border border-sand/10 shadow-2xl shadow-black/50">
            <motion.img
              src={HERO_IMG}
              alt="Florida home exterior"
              style={{ y: imgY, scale: 1.15 }}
              className="w-full h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-obsidian/20" />
            <div className="absolute inset-0 radar-sweep rounded-full" style={{ clipPath: "circle(45% at 50% 50%)" }} />
            <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
              <line x1="50%" y1="0" x2="50%" y2="100%" stroke="rgba(247,245,240,0.15)" strokeWidth="1" strokeDasharray="4 6" />
              <line x1="0" y1="50%" x2="100%" y2="50%" stroke="rgba(247,245,240,0.15)" strokeWidth="1" strokeDasharray="4 6" />
            </svg>
            {points.map((p) => (
              <button
                key={p.code}
                data-testid={`hero-scan-point-${p.code.toLowerCase()}`}
                onMouseEnter={() => setActive(p.code)}
                onMouseLeave={() => setActive(null)}
                className="absolute group"
                style={{ left: p.x, top: p.y }}
              >
                <span className="absolute -inset-3 rounded-full bg-sun/30 ping-ring" />
                <span className="relative block w-3.5 h-3.5 rounded-full bg-sun border-2 border-obsidian" />
                {active === p.code && (
                  <motion.span
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute left-5 top-1/2 -translate-y-1/2 whitespace-nowrap bg-obsidian/95 backdrop-blur border border-sand/15 rounded-md px-3 py-2 z-10"
                  >
                    <span className="block font-mono text-[9px] tracking-[0.25em] text-sun">{p.code}</span>
                    <span className="block font-display font-bold text-xs text-sand mt-0.5">{p.label}</span>
                  </motion.span>
                )}
              </button>
            ))}
          </div>
          <div className="absolute -bottom-5 -left-5 bg-sand rounded-xl px-5 py-4 shadow-xl">
            <div className="font-mono text-[10px] tracking-[0.25em] text-obsidian/50 uppercase">Live Scan</div>
            <div className="font-display font-extrabold text-obsidian text-lg leading-tight">300+ Checkpoints</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
